/**
 * Keeping the app the size of the part of the screen you can actually see.
 *
 * On a phone the keyboard slides up *over* the page rather than resizing it.
 * The layout viewport stays the full height of the screen, so the composer -
 * pinned to the bottom of a full-height flex column - ends up underneath the
 * keys, and because the layout does not scroll there is no way to reach it.
 * That is the "text input is stuck" behaviour.
 *
 * visualViewport reports the visible rectangle instead, keyboard subtracted.
 * Writing its height into a variable and sizing the app off that means the
 * composer rides up with the keyboard the way it does in every other chat
 * app. On a desktop this never fires and the variable stays unset, so the
 * stylesheet's own 100dvh applies.
 */

import { useEffect, useState } from 'react';

export function trackViewport(): void {
  const vv = window.visualViewport;
  if (!vv) return;

  let frame = 0;

  const apply = () => {
    frame = 0;
    // Only while the keyboard is actually taking space. Otherwise the value
    // is left alone, so rotating or hiding the address bar does not fight
    // with the stylesheet.
    const covered = window.innerHeight - vv.height;
    if (covered > 80) {
      document.documentElement.style.setProperty('--app-h', vv.height + 'px');
      document.body.dataset.keyboard = '1';
    } else {
      document.documentElement.style.removeProperty('--app-h');
      delete document.body.dataset.keyboard;
    }
  };

  // Resize fires many times as the keyboard animates in; one write per frame
  // is plenty and keeps the layout from thrashing.
  const schedule = () => { if (!frame) frame = requestAnimationFrame(apply); };

  vv.addEventListener('resize', schedule);
  vv.addEventListener('scroll', schedule);
  apply();
}


/* ------------------------------------------------------------------ widths */

/**
 * Width breakpoints, as a hook.
 *
 * The app had one breakpoint - useNarrow at 768px - and treated everything
 * above it as the same desktop. That is how Chat ended up with a 156px
 * conversation at 1092px: a 248px rail, a 320px list and a 320px details panel
 * all laid out as if the window were 1600px wide.
 *
 * matchMedia rather than a resize listener, so it fires once when a threshold
 * is crossed instead of on every pixel of a window drag.
 */
export function useMinWidth(px: number): boolean {
  const query = '(min-width: ' + px + 'px)';
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);

  return matches;
}

/** The same, for the height of the window. */
export function useMinHeight(px: number): boolean {
  const query = '(min-height: ' + px + 'px)';
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);

  return matches;
}

/*
 * The thresholds, named once.
 *
 * RAIL_FULL - below this the sidebar is an icon rail, giving 172px back.
 * CHAT_DETAILS_INLINE - details sit beside the conversation only when it can
 *   stay ~560px wide with them open: rail 248 + list 320 + details 320 +
 *   gaps ~50 + 560 = 1498.
 * CHAT_LIST_WIDE - the conversation list drops from 320px to 280px below this.
 */
export const RAIL_FULL = 1200;
/* Lowered from 1500 so the three columns in the chat reference - list,
   conversation, profile - show at an ordinary 1440 window too. */
export const CHAT_DETAILS_INLINE = 1360;
export const CHAT_LIST_WIDE = 1280;
